import React, { forwardRef, useCallback, useId } from "react";
import clsx from "clsx";

type CheckboxBaseProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "type">;

interface CheckboxProps extends CheckboxBaseProps {
  className?: string;
  containerClassName?: string;
  boxClassName?: string;
  children?: React.ReactNode;
  checkedColor?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  indeterminate?: boolean;
  boxSize?: "sm" | "md" | "lg";
}

const sizeClasses = {
  sm: "h-2 w-2",
  md: "h-2.5 w-2.5",
  lg: "h-3 w-3",
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, containerClassName, boxClassName, children, onChange, checked, checkedColor, disabled, indeterminate = false, boxSize = "md", id, ...rest }, ref) => {
    const generatedId = useId();
    const inputId = id || generatedId;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!disabled && onChange) {
          onChange(e, e.target.checked);
        }
      },
      [disabled, onChange]
    );

    const isChecked = checked ?? false;

    return (
      <label htmlFor={inputId} className={clsx("checkbox", !disabled ? "cursor-pointer" : "opacity-50 cursor-not-allowed", containerClassName)}>
        <input ref={ref} id={inputId} type="checkbox" className="hidden" checked={isChecked} onChange={handleChange} disabled={disabled} aria-checked={indeterminate ? "mixed" : isChecked} {...rest} />
        <span className={clsx(className, "flex-center")}>
          <div className={clsx("flex-center p-[1px] rounded-sm border border-black bg-black-300", sizeClasses[boxSize], boxClassName)}>
            {indeterminate ? (
              <div className={clsx("w-1/2 h-0.5", checkedColor || "bg-black-300")} />
            ) : (
              isChecked && <div className={clsx("w-full h-full rounded-[1px]", checkedColor || "bg-black-300")} />
            )}
          </div>
        </span>
        {children && <div className="flex">{children}</div>}
      </label>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
